import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { errorToast, infoToast } from 'src/utils/toasts';
import { getAccessToken } from 'utils/authUtils';
import { getStoredImpersonatedUserId } from 'store/user/impersonationStorage';
import { shouldTraceRequest, traceLog } from 'src/utils/requestTrace';

declare module 'axios' {
  export interface AxiosRequestConfig {
    // Opt-out for the global 400 validation-error toast. Call sites that render
    // their own inline 400 handling (PIN entry, account linking, Scribe HITL
    // approve/reject) set this so users don't get a duplicate/leaky toast.
    suppressValidationToast?: boolean;
    // Opt-out of 429 auto-retry. Background polls MUST set this: retrying a
    // rate-limited poll just pours more requests onto the tripped limiter while
    // the interval keeps firing new ones (a self-DoS feedback loop). A poll
    // should skip the rejected tick and try again on its next natural interval.
    skipRateLimitRetry?: boolean;
  }
}

type RateLimitConfig = AxiosRequestConfig & {
  __retryCount?: number;
  __rateLimited?: boolean;
};

const COMPENDIUM_TIER_REQUIRED_CODE = 'COMPENDIUM_TIER_REQUIRED';
export const COMPENDIUM_TIER_REQUIRED_EVENT = 'compendium-tier-required';
const MAX_RATE_LIMIT_RETRIES = 3;
const BASE_BACKOFF_MS = 1000;

let rateLimitToastActive = false;

const parseRetryAfter = (headerValue?: string): number | null => {
  if (!headerValue) return null;
  const seconds = Number(headerValue);
  if (!Number.isNaN(seconds)) return seconds * 1000;
  const dateMs = Date.parse(headerValue);
  if (Number.isNaN(dateMs)) return null;
  const delay = dateMs - Date.now();
  return delay > 0 ? delay : null;
};

const getBackoffDelayMs = (error: AxiosError, retryCount: number): number | null => {
  if (retryCount >= MAX_RATE_LIMIT_RETRIES) return null;
  const retryAfterHeader = error.response?.headers?.['retry-after'];
  const retryAfterDelay = parseRetryAfter(
    Array.isArray(retryAfterHeader) ? retryAfterHeader[0] : retryAfterHeader
  );
  if (retryAfterDelay && retryAfterDelay > 0) return retryAfterDelay;
  return BASE_BACKOFF_MS * 2 ** retryCount;
};

const isAuthEndpoint = (url: string) =>
  url.includes('/users/me') || url.includes('/users/feature-flags');

const isCompendiumTierRequiredResponse = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;

  const record = data as Record<string, unknown>;
  const details = record.details as Record<string, unknown> | undefined;
  const error = record.error as Record<string, unknown> | undefined;
  const errorDetails = error?.details as Record<string, unknown> | undefined;

  return (
    record.code === COMPENDIUM_TIER_REQUIRED_CODE ||
    details?.code === COMPENDIUM_TIER_REQUIRED_CODE ||
    error?.code === COMPENDIUM_TIER_REQUIRED_CODE ||
    errorDetails?.code === COMPENDIUM_TIER_REQUIRED_CODE
  );
};

const setupResponseInterceptors = (
  instance: AxiosInstance,
  options: { redirectOnAuthError: boolean }
) => {
  instance.interceptors.response.use(
    (response) => {
      const cfg = response.config as RateLimitConfig;
      if (cfg.__rateLimited) rateLimitToastActive = false;
      return response;
    },
    async (error: AxiosError) => {
      const status = error.response?.status;
      const url = error.response?.config?.url ?? '';

      if (options.redirectOnAuthError && error.response) {
        const isAuthError = status === 401 || (status === 400 && isAuthEndpoint(url));
        if (isAuthError) {
          console.error('Authentication error, redirecting to login:', error);
          window.location.assign('/login');
          return Promise.reject(error);
        }
      }

      if (status === 403) {
        if (isCompendiumTierRequiredResponse(error.response?.data)) {
          window.dispatchEvent(new Event(COMPENDIUM_TIER_REQUIRED_EVENT));
          return Promise.reject(error);
        }

        console.error('Forbidden:', error);
        const title = url.split('/')[1] ?? '';
        errorToast({
          title: 'Forbidden',
          description: `You do not have permission to access this resource. ${title}`
        });
      }

      // Handle validation errors (400 with validation details), unless the call
      // site opted out because it renders its own inline 400 handling.
      if (status === 400 && error.response?.data && !error.config?.suppressValidationToast) {
        const data = error.response.data as Record<string, unknown>;
        // Check both possible locations for issues array
        const errorDetails = data?.error as Record<string, unknown> | undefined;
        const issues =
          (errorDetails?.details as Record<string, unknown>)?.issues ??
          (data?.details as Record<string, unknown>)?.issues;

        if (Array.isArray(issues) && issues.length > 0) {
          const messages = issues
            .map((issue: { message?: string }) => issue.message)
            .filter(Boolean)
            .join(', ');
          if (messages) {
            errorToast({
              title: 'Validation Error',
              description: messages
            });
          }
        } else if (typeof data?.message === 'string') {
          // Simple error format: { message: "..." }
          errorToast({
            title: 'Validation Error',
            description: data.message
          });
        }
      }

      if (status === 429 && error.config) {
        const cfg = error.config as RateLimitConfig;

        // Background polls opt out of retry: let the next interval tick handle
        // it instead of amplifying load on the limiter.
        if (cfg.skipRateLimitRetry) {
          return Promise.reject(error);
        }

        const retryCount = cfg.__retryCount ?? 0;
        const delayMs = getBackoffDelayMs(error, retryCount);

        if (!rateLimitToastActive) {
          infoToast({
            title: 'Rate limited',
            description: 'Retrying shortly'
          });
          rateLimitToastActive = true;
        }

        if (delayMs === null) {
          rateLimitToastActive = false;
          errorToast({
            title: 'Rate limit reached',
            description: 'Please try again in a moment.'
          });
          return Promise.reject(error);
        }

        cfg.__retryCount = retryCount + 1;
        cfg.__rateLimited = true;

        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return instance(cfg);
      }

      return Promise.reject(error);
    }
  );
};

const api = axios.create({
  baseURL: import.meta.env.VITE_API_SERVER_URL
});

const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_SERVER_URL
});

api.interceptors.request.use(async (config) => {
  const requestUrl = config.url ?? '';
  if (shouldTraceRequest(requestUrl)) {
    traceLog('api', 'request', {
      method: config.method?.toUpperCase() ?? 'GET',
      url: requestUrl,
      source: config.headers?.['X-Debug-Source']
    });
  }

  const token = await getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const impersonatedUserId = getStoredImpersonatedUserId();
  if (impersonatedUserId && config.headers) {
    config.headers['X-Impersonate-User-Id'] = impersonatedUserId;
  }
  return config;
});

setupResponseInterceptors(api, { redirectOnAuthError: true });
setupResponseInterceptors(publicApi, { redirectOnAuthError: false });

export default api;
export { publicApi };
